import { Chess as ChessJs, validateFen, type Move as ChessJsMove } from 'chess.js';
import type { Chess, MoveInput } from './interface.ts';
import { type Board, type Color, type Fen, type Move, type ParsedGame, type PieceType, type Position, type San, type Square } from '@chess/api-model';

export const CHESSJS_VERSION = '1.4.0';

const START: Fen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function color(c: 'w' | 'b'): Color {
  return c === 'w' ? 'white' : 'black';
}

function toMove(m: ChessJsMove): Move {
  const flags = m.flags;
  return {
    from: m.from,
    to: m.to,
    san: m.san,
    uci: m.from + m.to + (m.promotion ?? ''),
    color: color(m.color),
    piece: m.piece as PieceType,
    captured: m.captured as PieceType | undefined,
    promotion: m.promotion as PieceType | undefined,
    flags: {
      capture: flags.includes('c') || flags.includes('e'),
      castle: flags.includes('k') || flags.includes('q'),
      enPassant: flags.includes('e'),
      promotion: flags.includes('p'),
      check: m.san.endsWith('+') || m.san.endsWith('#'),
      checkmate: m.san.endsWith('#'),
    },
  };
}

function load(fen: Fen): ChessJs {
  return new ChessJs(fen);
}

export function createChessJs(): Chess {
  return {
    startFen: START,

    validateFen(fen) {
      return validateFen(fen).ok;
    },

    position(fen): Position {
      const game = load(fen);
      return {
        fen: game.fen(),
        turn: color(game.turn()),
        fullmove: game.moveNumber(),
        halfmove: Number(fen.split(' ')[4] ?? 0),
        check: game.isCheck(),
        checkmate: game.isCheckmate(),
        stalemate: game.isStalemate(),
        draw: game.isDraw(),
        gameOver: game.isGameOver(),
      };
    },

    board(fen): Board {
      const board: Board = {};
      for (const row of load(fen).board()) {
        for (const cell of row) {
          if (cell) board[cell.square as Square] = { color: color(cell.color), type: cell.type as PieceType };
        }
      }
      return board;
    },

    legalMoves(fen, from?: Square) {
      const game = load(fen);
      const moves = from ? game.moves({ square: from as never, verbose: true }) : game.moves({ verbose: true });
      return moves.map(toMove);
    },

    play(fen, move: San | MoveInput) {
      const game = load(fen);
      try {
        const played = game.move(move as never);
        return { move: toMove(played), fen: game.fen() };
      } catch {
        return undefined;
      }
    },

    parsePgn(pgn): ParsedGame {
      const game = new ChessJs();
      game.loadPgn(pgn);
      const history = game.history({ verbose: true });
      const positions: Fen[] = history.length ? [history[0].before, ...history.map((m) => m.after)] : [game.fen()];
      const headers = game.getHeaders();
      const result = headers.Result;
      return {
        headers,
        moves: history.map(toMove),
        positions,
        result: result === '1-0' || result === '0-1' || result === '1/2-1/2' ? result : '*',
      };
    },

    toPgn(headers, sans) {
      const game = new ChessJs();
      for (const [key, value] of Object.entries(headers)) game.setHeader(key, value);
      for (const san of sans) game.move(san);
      return game.pgn();
    },
  };
}
